import { StorageHelper } from '@smg-real-estate/hg-search-libs';

import { StorageNamespace } from '@/helpers/storage-namespaces';
import { HgLocalStorageKey } from '@/models/HgLocalStorageKey';

export enum TenantPlusModalOpenTrigger {
  CONTACT_FORM = 'contactForm',
  PHONE_NUMBER = 'phoneNumber',
  LISTER_PHONE_NUMBER = 'listerPhoneNumber',
  APPOINTMENT_VIEWING = 'appointmentViewing',
  REQUEST_ADDRESS = 'requestAddress',
  LISTING_STATS = 'listingStats',
  CRIF_MODAL = 'crifModal',
  BOTTOMSHEET = 'bottomsheet',
  MORE_IMAGES = 'moreImages',
  DESCRIPTION_CONTACT = 'desc_hide_contact',
}

export enum TenantPlusFeature {
  CONTACT_BLOCK = 'contactBlock',
  LISTING_DEMAND = 'listingDemand',
  EXCLUSIVE_IMAGES = 'exclusiveImages',
}

/**
 * Tracking suffix per modal trigger (without page prefix).
 * Used for h_funnel_step_input_141 and entry points like `pdp_rent_listing_stats`.
 */
export const tenantPlusTrackingSuffix: Record<TenantPlusModalOpenTrigger, string> = {
  [TenantPlusModalOpenTrigger.CONTACT_FORM]: 'contact_form',
  [TenantPlusModalOpenTrigger.PHONE_NUMBER]: 'phone_number',
  [TenantPlusModalOpenTrigger.LISTER_PHONE_NUMBER]: 'lister_phone_number',
  [TenantPlusModalOpenTrigger.APPOINTMENT_VIEWING]: 'appointment_viewing',
  [TenantPlusModalOpenTrigger.REQUEST_ADDRESS]: 'request_address',
  [TenantPlusModalOpenTrigger.LISTING_STATS]: 'listing_stats',
  [TenantPlusModalOpenTrigger.CRIF_MODAL]: 'crif_pdp_bottomsheet',
  [TenantPlusModalOpenTrigger.BOTTOMSHEET]: 'bottom_sheet',
  [TenantPlusModalOpenTrigger.MORE_IMAGES]: 'blur_image',
  [TenantPlusModalOpenTrigger.DESCRIPTION_CONTACT]: 'desc_hide_contact',
};

const getTrackingSuffixFromEntryPoint = (entryPoint: string): string | undefined => {
  const match = /^(?:pdp|srp)_(?:rent|buy)_(.+)$/.exec(entryPoint);

  return match?.[1];
};

/** Resolves `TenantPlusModalOpenTrigger` from a tracking suffix such as `listing_stats`. */
export const getTenantPlusModalOpenTriggerBySuffix = (
  suffix: string,
): TenantPlusModalOpenTrigger | undefined => {
  const triggerEntry = Object.entries(tenantPlusTrackingSuffix).find(
    ([, value]) => value === suffix,
  );

  return triggerEntry?.[0] as TenantPlusModalOpenTrigger | undefined;
};

/**
 * Builds h_event_label_008 from a funnel entry point.
 * Example: `pdp_rent_listing_stats` → `tenantPlus_trial_listingStats`.
 */
export const getTenantPlusEventLabelFromEntryPoint = (entryPoint: string): string | undefined => {
  const suffix = getTrackingSuffixFromEntryPoint(entryPoint);
  const modalOpenTrigger = suffix ? getTenantPlusModalOpenTriggerBySuffix(suffix) : undefined;

  if (!modalOpenTrigger) {
    return undefined;
  }

  return `tenantPlus_trial_${modalOpenTrigger}`;
};

export const getWelcomeTenantPlusEntryPoint = (): string | undefined => {
  const tenantPlusStorageHelper = new StorageHelper(StorageNamespace.tenantPlus);
  return tenantPlusStorageHelper.getLocalStorageItem(HgLocalStorageKey.ENTRY_POINT);
};

/**
 * Reads `tenant-plus.entryPoint` from localStorage after a T+ redirect
 * and returns the matching h_event_label_008 value.
 */
export const getWelcomeTenantPlusEventLabel = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const entryPoint = getWelcomeTenantPlusEntryPoint();

  if (!entryPoint) {
    return undefined;
  }

  return getTenantPlusEventLabelFromEntryPoint(entryPoint);
};
